"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Label } from "../ui/label"
import { Checkbox } from "../ui/checkbox"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Save, Plus, X, Search, Edit } from "lucide-react"
import { Badge } from "../ui/badge"
import { useRef } from "react"
import { toast } from "sonner"

// --- Interfaces ---

interface Manager {
  id: number
  name: string
}

interface User {
  id: number
  name: string
  email: string
  isActive: boolean
}

// 🚀 NEW INTERFACE: Task object now stores richer data
interface Task { 
    id: number | null // TaskID from master list
    engagementTaskID?: number | null // Existing engagement-task mapping ID
    taskName: string
    taskDescription: string
    isGeneric: boolean // Flag to check if it's a generic/custom task (useful for blue text)
    modUser: number | null // User ID of the modifier/creator
    modifiedBy: string
    createdBy: string | null
    createdById?: number | null
    isCreatedByCurrentUser?: boolean
}

interface CreateTaskRequest {
  engagementTaskID: number
  engagementID: number
  taskID: number
  taskName: string
  taskDescription: string
  isDeleted: boolean
  isGeneric: boolean
  modUser: number
}

interface AddEngagementModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: any) => void
  initialData?: any
  mode?: "add" | "edit" | "copy"
  managers?: Manager[]
  users?: User[]
  currentUser?: User | null
  onCreateTask?: (taskData: CreateTaskRequest) => Promise<any>
  onDeleteTask?: (taskId: number) => Promise<any>
}

interface TeamMemberTableData {
  id: string
  name: string
  startDate: string
  endDate: string
  startDateManuallyEdited?: boolean
  endDateManuallyEdited?: boolean
}

// -----------------------------------------------------
// 🎯 UPDATED COMPONENT: AddEngagementModal
// -----------------------------------------------------

export function AddEngagementModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  mode = "add",
  managers = [],
  users = [],
  currentUser = null,
  onCreateTask,
  onDeleteTask,
}: AddEngagementModalProps) {
  
  // 🚀 NEW STATE for Edit Task Modal
  const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);

  // Updated selectedTasks to hold Task objects
  const [formData, setFormData] = useState({
    project: "",
    startDate: "",
    endDate: "",
    description: "",
    projectOwners: [] as string[],
    teamMembers: [] as string[],
    selectedTasks: [] as Task[], // 👈 Changed to Task[]
  })

  const [newTask, setNewTask] = useState("")
  useEffect(() => {
    if (!isOpen) {
      setNewTask(""); // clear the "Add Task" input
    }
  }, [isOpen]);

const [searchTerm, setSearchTerm] = useState("");
const [dropdownOpen, setDropdownOpen] = useState(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);

  const [teamMembersTableData, setTeamMembersTableData] = useState<TeamMemberTableData[]>([])
  const [validationErrors, setValidationErrors] = useState<{
    project?: string;
    startDate?: string;
    endDate?: string;
    description?: string;
  }>({})

  const availableProjectOwners = managers.length > 0 ? managers : [
    { id: 1, name: "Loading..." }
  ]

  const availableTeamMembers = users.length > 0 ? users : [
    { id: 1, name: "Loading..." }
  ]

  // 🚀 UPDATED STATE: allTasks now stores Task objects
  const [allTasksFromApi, setAllTasksFromApi] = useState<Task[]>([]) 
  const sessionUserName = useMemo(() => {
    if (typeof window === "undefined") {
      return currentUser?.name ?? null;
    }
    try {
      return sessionStorage.getItem("userName") || localStorage.getItem("userName") || currentUser?.name || null;
    } catch {
      return currentUser?.name || null;
    }
  }, [currentUser]);
  // Keep team member dates in sync with engagement dates unless manually overridden
  useEffect(() => {
    if (!isOpen) return;
    setTeamMembersTableData(prev =>
      prev.map(member => {
        const updatedMember = { ...member };
        if (!member.startDateManuallyEdited) {
          updatedMember.startDate = formData.startDate || "";
        }
        if (!member.endDateManuallyEdited) {
          updatedMember.endDate = formData.endDate || "";
        }
        return updatedMember;
      })
    );
  }, [formData.startDate, formData.endDate, isOpen]);
  
  // Helper function to unify API response shape
  const mapApiTaskToTask = useCallback((apiTask: any): Task => {
    const getNumericId = (value: any) => {
      if (value === null || value === undefined) return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const modUserId = getNumericId(apiTask?.modUser ?? apiTask?.modifiedById ?? apiTask?.modifiedByID);
    const createdById = getNumericId(apiTask?.createdById ?? apiTask?.createdByID ?? apiTask?.creatorId);

    const createdByName = apiTask?.createdBy ?? apiTask?.modifiedBy ?? sessionUserName ?? currentUser?.name ?? "Unknown";
    const modifiedByName = apiTask?.modifiedBy ?? apiTask?.createdBy ?? sessionUserName ?? currentUser?.name ?? "Unknown";

    const isMine = Boolean(
      (currentUser?.id && (modUserId === currentUser.id || createdById === currentUser.id)) ||
      (sessionUserName &&
        ((createdByName?.trim().toLowerCase() ?? "") === sessionUserName.trim().toLowerCase() ||
        (modifiedByName?.trim().toLowerCase() ?? "") === sessionUserName.trim().toLowerCase()))
    );

    return {
      id: apiTask?.taskID ?? null,
      engagementTaskID: apiTask?.engagementTaskID ?? null,
      taskName: apiTask?.taskName ?? "",
      taskDescription: apiTask?.taskDescription ?? apiTask?.taskName ?? "",
      isGeneric: apiTask?.isGeneric === true,
      modUser: modUserId,
      modifiedBy: modifiedByName || "Unknown",
      createdBy: createdByName || null,
      createdById: createdById ?? modUserId,
      isCreatedByCurrentUser: isMine,
    };
  }, [currentUser?.id, sessionUserName]);

  //for project owner dropdown
useEffect(() => {
  const handler = (e: MouseEvent) => {
    if (!(e.target as HTMLElement).closest(".task-dropdown-container")) {
      setDropdownOpen(false)
    }
  }

  document.addEventListener("click", handler)
  return () => document.removeEventListener("click", handler)
}, [])

const dropdownRef = useRef<HTMLDivElement | null>(null);

useEffect(() => {
  function handleClickOutside(event: MouseEvent) {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setTeamDropdownOpen(false);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

const fetchTasks = async () => {
      try {
        const res = await fetch(process.env.REACT_APP_API_URL +"/api/Engagement/tasks", {
          method: "GET",
          credentials: "include", 
        });
        const text = await res.text(); 
        const data = JSON.parse(text); 
        ////console.log("Raw API task response:", data);
        
        const apiTasks: Task[] = data.tasks 
          ? data.tasks.map(mapApiTaskToTask) 
          : [];

        setAllTasksFromApi(apiTasks);

        // 🚀 Map initialData.tasks to the new Task[] interface
        //This ensures tasks that were previously selected (including newly created ones) remain selected
        if (initialData?.tasks) {
            const initialSelectedTasks: Task[] = initialData.tasks.map((t: any) => {
                // If data is just a string (old format), find the full object or create a placeholder
                if (typeof t === 'string') {
                    const foundTask = apiTasks.find(at => at.taskName === t);
                    if (foundTask) return foundTask; // Use API task which has correct metadata
                    return { 
                        id: null, 
                        taskName: t, 
                        taskDescription: t, 
                        isGeneric: true, 
                        modUser: currentUser?.id || 1, 
                        modifiedBy: sessionUserName || currentUser?.name || "Unknown",
                        createdBy: sessionUserName || currentUser?.name || null,
                    };
                }
                
                // If it's already an object, try to find the matching task in apiTasks first
                // This ensures we use the most up-to-date task data from the API
                // Match by taskID first, then engagementTaskID, then taskName
                const taskId = t.taskID ?? t.id;
                const engagementTaskId = t.engagementTaskID ?? t.engagementTaskID;
                const taskName = t.taskName;
                
                // Try to find matching task in API tasks by ID (most reliable)
                if (taskId) {
                    const foundById = apiTasks.find(at => at.id === taskId);
                    if (foundById) return foundById;
                }
                
                // Try by engagementTaskID
                if (engagementTaskId) {
                    const foundByEngagementTaskId = apiTasks.find(at => at.engagementTaskID === engagementTaskId);
                    if (foundByEngagementTaskId) return foundByEngagementTaskId;
                }
                
                // Try by taskName (fallback)
                if (taskName) {
                    const foundByName = apiTasks.find(at => at.taskName === taskName);
                    if (foundByName) return foundByName;
                }
                
                // If not found in API tasks, map the initialData task (preserves selection state)
                return mapApiTaskToTask(t);
            });
            setFormData(prev => ({ ...prev, selectedTasks: initialSelectedTasks }));
        }

          // ✅ ADD THIS: Only set initial selected tasks if we haven't already set them
          // if (initialData?.tasks && formData.selectedTasks.length === 0) {
          //   const initialSelectedTasks: Task[] = initialData.tasks.map((t: any) => {
          //       // If data is just a string (old format), find the full object or create a placeholder
          //       if (typeof t === 'string') {
          //           const foundTask = apiTasks.find(at => at.taskName === t);
          //           if (foundTask) return foundTask; // Use API task which has correct metadata
          //           return { 
          //               id: null, 
          //               taskName: t, 
          //               taskDescription: t, 
          //               isGeneric: true, 
          //               modUser: currentUser?.id || 1, 
          //               modifiedBy: sessionUserName || currentUser?.name || "Unknown",
          //               createdBy: sessionUserName || currentUser?.name || null,
          //           };
          //       }
                

            //     const taskId = t.taskID ?? t.id;
            //     const engagementTaskId = t.engagementTaskID ?? t.engagementTaskID;
            //     const taskName = t.taskName;

            //     if (taskId) {
            //         const foundById = apiTasks.find(at => at.id === taskId);
            //         if (foundById) return foundById;
            //     }

            //     if (engagementTaskId) {
            //         const foundByEngagementTaskId = apiTasks.find(at => at.engagementTaskID === engagementTaskId);
            //         if (foundByEngagementTaskId) return foundByEngagementTaskId;
            //     }

            //     if (taskName) {
            //         const foundByName = apiTasks.find(at => at.taskName === taskName);
            //         if (foundByName) return foundByName;
            //     } 
            //     return mapApiTaskToTask(t);
            // });
            // setFormData(prev => ({ ...prev, selectedTasks: initialSelectedTasks }));
        //}
      } catch (err) {
        console.error("Failed to fetch tasks:", err);
        setAllTasksFromApi([]);
      }
    };


  // 💡 API Fetch and Initial Data useEffect
  useEffect(() => {
    if (!isOpen) {
      setValidationErrors({});
      return;
    }

    fetchTasks();

    // Reset non-task form data on open or initialData change
    if (initialData) {
        // This is complex, so for brevity, I'll assume the rest of your useEffect logic 
        // that handles project/owner/member data remains, but will focus on the task part:
        const parsedOwners = initialData.owners ? initialData.owners.map((owner: any) => String(owner.userID || owner.id)) : [];
        const parsedTeamMembers = initialData.teamMembers ? initialData.teamMembers.map((member: any) => String(member.userID || member.id)) : [];
        
        setFormData(prev => ({
            ...prev,
            project: initialData.title || "",
            startDate: initialData.startDate ? initialData.startDate.split('T')[0] : "", 
            endDate: initialData.endDate ? initialData.endDate.split('T')[0] : "",
            description: initialData.description || "",
            projectOwners: parsedOwners,
            teamMembers: parsedTeamMembers,
            // selectedTasks will be set by the fetchTasks function above
        }));
        
        // Re-calculate team members table data here if needed (copied from your original)
        const initialTableMembers = parsedTeamMembers.map((memberId: string) => {
            const member = availableTeamMembers.find((m) => m.id === Number(memberId))
            const memberName = member ? member.name : `User ${memberId}`
            const initialMemberData = initialData.teamMembers.find(
                (m: any) => String(m.userID || m.id) === memberId
            );
          
            return {
              id: String(member?.id || memberId),
              name: memberName,
              startDate: initialMemberData?.startDate ? initialMemberData.startDate.split('T')[0] : initialData.startDate.split('T')[0] || "",
              endDate: initialMemberData?.endDate ? initialMemberData.endDate.split('T')[0] : initialData.endDate.split('T')[0] || "",
              startDateManuallyEdited: false,
              endDateManuallyEdited: false,
            }
        });
        setTeamMembersTableData(initialTableMembers);

    } else if (isOpen && mode === "add") {
        const currentUserOwners = currentUser ? [String(currentUser.id)] : [];
        const currentUserMembers = currentUser ? [String(currentUser.id)] : [];
        
        setFormData(prev => ({
            ...prev,
            project: "",
            startDate: "",
            endDate: "",
            description: "",
            projectOwners: currentUserOwners,
            teamMembers: currentUserMembers,
            selectedTasks: [],
        }));
        
        const currentUserTableData = currentUser ? [{
            id: String(currentUser.id),
            name: currentUser.name,
            startDate: "",
            endDate: "",
            startDateManuallyEdited: false,
            endDateManuallyEdited: false,
        }] : [];
        setTeamMembersTableData(currentUserTableData);
    }

  }, [isOpen, initialData, currentUser, mode, availableTeamMembers]); // Dependencies for initial data load


  // 🔄 Handlers for Task Selection
  const handleTaskToggle = (taskName: string) => {
    setFormData((prev) => {
      const isSelected = prev.selectedTasks.some((t: Task) => t.taskName === taskName);
      
      if (isSelected) {
        // Deselect
        return {
          ...prev,
          selectedTasks: prev.selectedTasks.filter((t: Task) => t.taskName !== taskName),
        };
      } else {
        // Select: Always use API task if available (has correct modifiedBy from backend)
        // Only create fallback if task doesn't exist in API (new task scenario)
        const apiTask = allTasksFromApi.find(t => t.taskName === taskName);
        const taskObj = apiTask || { 
            id: null,
            engagementTaskID: null,
            taskName,
            taskDescription: taskName,
            isGeneric: true,
            modUser: currentUser?.id || 1,
            modifiedBy: sessionUserName || currentUser?.name || "Unknown",
            createdBy: sessionUserName || currentUser?.name || null,
            createdById: currentUser?.id || null,
            isCreatedByCurrentUser: true,
        };
        return {
          ...prev,
          selectedTasks: [...prev.selectedTasks, taskObj],
        };
      }
    });
  }

  // ➕ Handlers for Adding New Task
  const handleAddTask = async () => {
    const trimmedTask = newTask.trim();
    if (!trimmedTask) return;

    // Check against all known task names (API and selected)
    const allKnownTaskNames = new Set([...allTasksFromApi.map(t => t.taskName), ...formData.selectedTasks.map(t => t.taskName)]);
    if (allKnownTaskNames.has(trimmedTask)) {
      toast.error("Task already exists", {
        description: "Try editing the existing task instead.",
      });
      return;
    }

    try {
      const payload: CreateTaskRequest = {
        engagementTaskID: 0,
        engagementID: initialData?.engagementID || 0,
        taskID: 0, // Should be set by backend on creation
        taskName: trimmedTask,
        taskDescription: trimmedTask, 
        isDeleted: false,
        isGeneric: true,
        modUser: currentUser?.id || 1,
      };

      const res = await fetch(process.env.REACT_APP_API_URL +"/api/Engagement/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(`Failed to add task: ${res.statusText}`);
      }

      const data = await res.json();
      ////console.log("Task created:", data);
      
      // Assume backend returns the full created task object with IDs
      const responseTask = data.task ?? {
        ...payload,
        createdBy: sessionUserName || currentUser?.name || "Unknown",
        modifiedBy: sessionUserName || currentUser?.name || "Unknown",
        createdById: currentUser?.id || null,
        engagementTaskID: data.engagementTaskID || null, // ✅ Get the engagement task ID from response
      };
      const newTaskObj: Task = {
        ...mapApiTaskToTask(responseTask),
        isCreatedByCurrentUser: true,
        createdById: currentUser?.id || responseTask.createdById || responseTask.modUser || null,
      };

      // Update frontend state immediately
      setAllTasksFromApi((prev) => [...prev, newTaskObj]); 
      setFormData((prev) => ({
        ...prev,
        selectedTasks: [...prev.selectedTasks, newTaskObj],
      }));

      setNewTask(""); // Clear input
      toast.success("Task added", {
        description: `"${trimmedTask}" has been added to this engagement.`,
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to add task", {
        description: "Please try again or contact support.",
      });
    }
  };


  // ✏️ NEW: Edit Task Handlers
  const handleOpenEditTaskModal = (task: Task) => {
      // Find the *latest* version of the task from selectedTasks (in case it was just edited)
      const latestTask = formData.selectedTasks.find(t => 
        (t.id && task.id && t.id === task.id) || t.taskName === task.taskName
      ) || task;
      setTaskToEdit(latestTask);
      setIsEditTaskModalOpen(true);
  }

  const handleUpdateTask = useCallback((updatedTask: Task, originalTask: Task | null) => {
    // Use the original task to find the task to update (since name might have changed)
    if (!originalTask) return;
    
    // Handle deletion case
    if ((updatedTask as any).isDeleted) {
      // Remove the task from both lists
      setAllTasksFromApi(prev => prev.filter(t => {
        if (originalTask.id && t.id === originalTask.id) return false;
        if (t.taskName === originalTask.taskName) return false;
        return true;
      }));
      
      setFormData(prev => ({
        ...prev,
        selectedTasks: prev.selectedTasks.filter(t => {
          if (originalTask.id && t.id === originalTask.id) return false;
          if (t.taskName === originalTask.taskName) return false;
          return true;
        })
      }));
      return;
    }
    
    // Handle update case
    // 1. Update the list of all available tasks (allTasksFromApi)
    // Match by ID first, then by original task name if ID is null
    setAllTasksFromApi(prev => 
        prev.map(t => {
          if (originalTask.id && t.id === originalTask.id) return updatedTask;
          if (!originalTask.id && originalTask.engagementTaskID && t.engagementTaskID === originalTask.engagementTaskID) return updatedTask;
          if (t.taskName === originalTask.taskName) return updatedTask;
          return t;
        })
    );

    // 2. Update the list of selected tasks (formData.selectedTasks)
    // Match by ID first, then by original task name if ID is null
    setFormData(prev => ({
        ...prev,
        selectedTasks: prev.selectedTasks.map(t => {
          if (originalTask.id && t.id === originalTask.id) return updatedTask;
          if (!originalTask.id && originalTask.engagementTaskID && t.engagementTaskID === originalTask.engagementTaskID) return updatedTask;
          if (t.taskName === originalTask.taskName) return updatedTask;
          return t;
        })
    }));
  }, []); 

  // 💾 Update handleSave to use the richer task data
  const handleSave = () => {
    // Validate required fields
    const errors: { project?: string; startDate?: string; endDate?: string; description?: string } = {};
    
    if (!formData.project.trim()) {
      errors.project = "Project title is required";
    }
    if (!formData.startDate) {
      errors.startDate = "Start date is required";
    }
    if (!formData.endDate) {
      errors.endDate = "End date is required";
    }
    if (!formData.description.trim()) {
      errors.description = "Project description is required";
    }
    if (formData.projectOwners.length === 0) {
      // Keep this as alert since it's not a field validation
      alert('At least one project owner is required');
      return;
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setValidationErrors({});


    // --- Date Formatting Logic (Copied from your code) ---
    const formatDateForAPI = (dateString: string) => {
        if (!dateString) { return new Date().toISOString(); }
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const date = new Date(dateString + 'T00:00:00.000Z');
            const now = new Date();
            date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
            return date.toISOString();
        }
        if (dateString.includes('T')) {
            if (!dateString.includes('.')) {
                const date = new Date(dateString);
                date.setMilliseconds(new Date().getMilliseconds());
                return date.toISOString();
            }
            return dateString;
        }
        try {
            const date = new Date(dateString);
            const now = new Date();
            date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
            return date.toISOString();
        } catch (e) {
            console.error('Failed to convert date:', dateString, e);
            return new Date().toISOString();
        }
    };
    // --- End Date Formatting Logic ---

    const engagementStartDate = formatDateForAPI(formData.startDate);
    const engagementEndDate = formatDateForAPI(formData.endDate);
    
    const savedData = {
      engagementID: initialData?.engagementID || 0,
      title: formData.project.trim(),
      startDate: engagementStartDate,
      endDate: engagementEndDate,
      description: formData.description.trim() || formData.project.trim(),
      owners: formData.projectOwners.length > 0 ? formData.projectOwners.map((id: string) => {
        const owner = availableProjectOwners.find((o) => o.id === Number(id));
        return {
          mappingID: 0,
          engagementID: initialData?.engagementID || 0,
          userID: Number(id),
          ownerName: owner?.name || `User ${id}`
        };
      }) : [{ mappingID: 0, engagementID: initialData?.engagementID || 0, userID: 1, ownerName: "Default Owner" }],
      teamMembers: teamMembersTableData.length > 0 ? teamMembersTableData.map((member) => ({
        mappingID: 0,
        userID: Number(member.id),
        teamMemberName: member.name,
        startDate: formatDateForAPI(member.startDate || formData.startDate),
        endDate: formatDateForAPI(member.endDate || formData.endDate),
        maxWeeklyHours: 40,
        createdBy: 1,
        modifiedBy: 1,
        modifiedOn: new Date().toISOString(),
        createdOn: new Date().toISOString(),
        engagementID: initialData?.engagementID || 0
      })) : [],
      // 💡 Use the richer Task objects for the tasks payload
      tasks: formData.selectedTasks.length > 0 ? formData.selectedTasks.map((task: Task) => ({
        engagementTaskID: task.engagementTaskID ?? 0,
        engagementID: initialData?.engagementID || 0,
        taskID: task.id ?? task.engagementTaskID ?? 0,
        taskName: task.taskName.trim(),
        taskDescription: task.taskDescription.trim(),
        isDeleted: false,
        isGeneric: task.isGeneric,
        modUser: task.modUser || currentUser?.id || 1
      })) : [],
      modUser: currentUser?.id || 1 
    };
    
    ////console.log('Final payload being sent to API:', JSON.stringify(savedData, null, 2));
    onSave(savedData);
    // Don't close modal here - let parent component handle closing on success
  }


  // ... (Owner/Member handlers remain the same) ...

  const handleOwnerSelect = (value: string) => {
    const shouldSyncOwner = mode === "add" && !formData.teamMembers.includes(value);

    setFormData((prev) => {
      const alreadyOwner = prev.projectOwners.includes(value);
      const needsMemberSync = mode === "add" && !prev.teamMembers.includes(value);

      if (alreadyOwner && !needsMemberSync) {
        return prev;
      }

      return {
        ...prev,
        projectOwners: alreadyOwner ? prev.projectOwners : [...prev.projectOwners, value],
        teamMembers: needsMemberSync ? [...prev.teamMembers, value] : prev.teamMembers,
      };
    });

    if (shouldSyncOwner) {
      setTeamMembersTableData((prev) => {
        if (prev.some((member) => member.id === value)) {
          return prev;
        }

        const ownerSource =
          availableTeamMembers.find((m) => m.id === Number(value)) ||
          availableProjectOwners.find((o) => o.id === Number(value));

        return [
          ...prev,
          {
            id: value,
            name: ownerSource?.name || `User ${value}`,
            startDate: formData.startDate || "",
            endDate: formData.endDate || "",
            startDateManuallyEdited: false,
            endDateManuallyEdited: false,
          },
        ];
      });
    }
  }

  const handleRemoveOwner = (idToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      projectOwners: prev.projectOwners.filter((id: string) => id !== idToRemove),
    }))
  }

  const handleTeamMemberSelect = (value: string) => {
    if (!formData.teamMembers.includes(value)) {
      const selectedMember = availableTeamMembers.find((m) => m.id === Number(value))
      if (selectedMember) {
        setFormData((prev) => ({
          ...prev,
          teamMembers: [...prev.teamMembers, value],
        }))
        setTeamMembersTableData((prev) => [
          ...prev,
          {
            id: String(selectedMember.id),
            name: selectedMember.name,
            startDate: formData.startDate || "",
            endDate: formData.endDate || "",
            startDateManuallyEdited: false,
            endDateManuallyEdited: false,
          },
        ])
      }
    }
  }

  const handleRemoveTeamMember = (idToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      teamMembers: prev.teamMembers.filter((id: string) => id !== idToRemove),
    }))
    setTeamMembersTableData((prev) => prev.filter((member) => member.id !== idToRemove))
  }


  // 💡 Use useMemo for filtering tasks based on taskName
  const allTaskNames = useMemo(() => allTasksFromApi.map(t => t.taskName), [allTasksFromApi]);
  const selectedTaskNames = useMemo(() => formData.selectedTasks.map(t => t.taskName), [formData.selectedTasks]);
  
  // Combine all unique task names for the filter list
  const combinedTaskNames = useMemo(() => {
    const names = new Set(allTaskNames); 
    selectedTaskNames.forEach(name => names.add(name));
    return Array.from(names);
  }, [allTaskNames, selectedTaskNames]);


  const filteredTaskNames = useMemo(() => {
    return combinedTaskNames.filter(
      (taskName) =>
        taskName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        selectedTaskNames.includes(taskName) // Always include selected tasks
    );
  }, [combinedTaskNames, searchTerm, selectedTaskNames]);

  // Find the full Task object for rendering
  const getTaskObjectByName = useCallback((taskName: string): Task => {
    // 1. Check selected tasks first (might contain local edits)
    const selectedTask = formData.selectedTasks.find(t => t.taskName === taskName);
    if (selectedTask) return selectedTask;

    // 2. Check API tasks
    const apiTask = allTasksFromApi.find(t => t.taskName === taskName);
    if (apiTask) return apiTask;
           
    // 3. Fallback (shouldn't happen often)
    return { 
        id: null, 
        engagementTaskID: null,
        taskName, 
        taskDescription: taskName, 
        isGeneric: true, 
        modUser: currentUser?.id || 1, 
        modifiedBy: sessionUserName || currentUser?.name || "Unknown",
        createdBy: sessionUserName || currentUser?.name || null,
        createdById: currentUser?.id || null,
        isCreatedByCurrentUser: Boolean(currentUser?.id || sessionUserName),
    };
  }, [formData.selectedTasks, allTasksFromApi, currentUser, sessionUserName]);


  // Check if task is modified by current user (for blue text display)
  // Always use API data as source of truth for modifiedBy to avoid stale data from selectedTasks
  const isTaskModifiedByCurrentUser = useCallback((taskName: string) => {
    // Prioritize API tasks as source of truth for modifiedBy
    const apiTask = allTasksFromApi.find(t => t.taskName === taskName);
    const task = apiTask || getTaskObjectByName(taskName);
    
    if (!task) return false;

    const currentName = (sessionUserName || currentUser?.name || "").trim().toLowerCase();
    if (!currentName) return false;

    const modifiedByName = task?.modifiedBy?.trim().toLowerCase();
    return modifiedByName === currentName;
  }, [allTasksFromApi, getTaskObjectByName, sessionUserName, currentUser]);

  const isTaskCreatedByCurrentUser = useCallback((taskName: string) => {
    const task = getTaskObjectByName(taskName);
    if (!task) return false;

    if (task.isCreatedByCurrentUser) {
      return true;
    }

    const currentId = currentUser?.id ?? null;

    if (currentId && (task.modUser === currentId || task.createdById === currentId)) {
      return true;
    }

    const currentName = (sessionUserName || currentUser?.name || "").trim().toLowerCase();
    if (!currentName) return false;

    const createdByName = task?.createdBy?.trim().toLowerCase();
    const modifiedByName = task?.modifiedBy?.trim().toLowerCase();
    return createdByName === currentName || modifiedByName === currentName;
  }, [getTaskObjectByName, sessionUserName, currentUser]);

  // Utility function (from your original code)
  const getModalTitle = () => {
    switch (mode) {
      case "edit":
        return "Edit Engagement"
      case "copy":
        return "Add New Engagement"
      default:
        return "Add New Engagement"
    }
  }

  // -----------------------------------------------------
// 🚀 NEW COMPONENT: EditTaskModal
// -----------------------------------------------------

  interface EditTaskModalProps {
      task: Task | null; // The task data being edited
      isOpen: boolean;
      onClose: () => void;
      onSave: (updatedTask: Task, originalTask: Task | null) => void;
      onDeleteTask?: (taskId: number) => void;
      currentUser: User | null;
  }

  function EditTaskModal({ task, isOpen, onClose, onSave, onDeleteTask, currentUser }: EditTaskModalProps) {
      // Local state for the task being edited
      const [editData, setEditData] = useState<Task | null>(task);
      const [validationError, setValidationError] = useState<string>("");

      // Update local state when 'task' prop changes
      useEffect(() => {
          if (!task) {
              setEditData(null);
              return;
          }

          const modifierName =
              sessionUserName ||
              currentUser?.name ||
              task.modifiedBy ||
              task.createdBy ||
              "Unknown";

          setEditData({
              ...task,
              modifiedBy: modifierName,
              createdBy: task.createdBy ?? modifierName,
          });
          setValidationError("");
      }, [task, currentUser, sessionUserName]);

      const handleTaskUpdateSave = async () => {
          if (!editData || !editData.taskName.trim()) {
              setValidationError("Task Name is required");
              return;
          }
          
          setValidationError("");

          const payload = {
              engagementTaskID: editData.id || 0,  // existing task ID
              engagementID: 0, // set engagement ID if needed
              taskID: editData.id || 0,
              taskName: editData.taskName.trim(),
              taskDescription: editData.taskDescription.trim() || editData.taskName.trim(),
              isGeneric: editData.isGeneric,
              modUser: currentUser?.id || 1,
          };

          try {
              const res = await fetch(process.env.REACT_APP_API_URL +"/api/Engagement/tasks", {
                  method: "POST", // assuming update uses PUT
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(payload),
                  credentials: "include",
              });

              if (!res.ok) {
                  throw new Error(`Failed to update task: ${res.statusText}`);
              }

              const updatedTaskResponse = await res.json();
              const normalizedTask = mapApiTaskToTask(updatedTaskResponse?.task ?? updatedTaskResponse ?? payload);

              // Merge the updated task with the current edit data to preserve all fields
              const mergedTask: Task = {
                  ...editData,
                  ...normalizedTask,
                  engagementTaskID: normalizedTask.engagementTaskID ?? editData.engagementTaskID ?? null,
                  taskName: normalizedTask.taskName || editData.taskName,
                  taskDescription: normalizedTask.taskDescription || editData.taskDescription,
                  isCreatedByCurrentUser:
                    editData.isCreatedByCurrentUser ||
                    (currentUser?.id
                      ? normalizedTask.modUser === currentUser.id || normalizedTask.createdById === currentUser.id
                      : false),
              };

              // Call parent onSave callback to update state in AddEngagementModal
              // Pass both the updated task and the original task for proper matching
              // This is the only way to update parent state - don't access formData/allTasksFromApi directly
              if (onSave) {
                  onSave(mergedTask, editData);
              }

              toast.success("Task updated", {
                description: `"${mergedTask.taskName}" changes have been saved.`,
              });
              onClose(); // Close the modal
          } catch (err) {
              console.error(err);
              toast.error("Failed to update task", {
                description: "Please try again or contact support.",
              });
          }
      };


      const handleDelete = async () => {
          if (!editData || !editData.id) return; // ✅ guard clause

          // Call parent callback if provided
          if (onDeleteTask) {
              onDeleteTask(editData.id);
          }

          try {
              const res = await fetch(process.env.REACT_APP_API_URL +`/api/Engagement/tasks/${editData.id}`, {
                  method: "DELETE",
                  credentials: "include",
              });

              if (!res.ok) throw new Error(`Failed to delete task: ${res.statusText}`);

              // Call onSave with deleted task and original task for parent to handle state updates
              // Parent will filter out the deleted task from both lists
              if (onSave) {
                  onSave({ ...editData, isDeleted: true } as Task, editData);
              }
              toast.success("Task deleted", {
                description: `"${editData.taskName}" has been removed.`,
              });
              
              onClose();
          } catch (err) {
              console.error(err);
              toast.error("Failed to delete task", {
                description: "Please try again or contact support.",
              });
          }
      };

      const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          setEditData(prev => prev ? { ...prev, taskName: e.target.value } : null);
      };

      const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          setEditData(prev => prev ? { ...prev, taskDescription: e.target.value } : null);
      };
      if (!editData) return null; // Don't render if no task data

      const creatorName =
          editData.modifiedBy ||
          editData.createdBy ||
          sessionUserName ||
          currentUser?.name ||
          "Unknown";

      return (
          <Dialog open={isOpen} onOpenChange={onClose}>
              <DialogContent className="sm:max-w-[425px]">
                  {/* <DialogHeader>
                      <DialogTitle>Edit Task: {editData.taskName}</DialogTitle>
                  </DialogHeader> */}
                  {/* <div className="grid gap-4 py-4"> 
                      
                      <div className="space-y-2">
                          <div className="flex items-center justify-between">
                              <Label htmlFor="taskName">Task Name *</Label>
                              <span className="text-xs text-gray-500">
                                  {editData.taskName.length}/250
                              </span>
                          </div>
                          <Input
                              id="taskName"
                              value={editData.taskName}
                              onChange={(e) => {
                                handleNameChange(e);
                                if (validationError) {
                                  setValidationError("");
                                }
                              }}
                              maxLength={250}
                              className={validationError ? 'border-red-500' : ''}
                          />
                          {validationError && (
                            <p className="text-red-500 text-xs mt-1">{validationError}</p>
                          )}
                      </div>
                      
                      <div className="space-y-2">
                          <div className="flex items-center justify-between">
                              <Label htmlFor="taskDescription">Description</Label>
                              <span className="text-xs text-gray-500">
                                  {editData.taskDescription.length}/500
                              </span>
                          </div>
                          <Textarea
                              id="taskDescription"
                              placeholder="Enter task description"
                              value={editData.taskDescription}
                              onChange={handleDescriptionChange}
                              className="min-h-[80px]"
                              maxLength={500}
                          />
                      </div>

                       <div className="text-sm text-gray-500">
                          Modified By: {creatorName}
                      </div> 
                  </div>*/}
                  {/* <DialogFooter>
                      <Button variant="destructive" onClick={handleDelete}>Delete</Button>
                      <Button onClick={handleTaskUpdateSave} className="bg-green-600 hover:bg-green-700">
                          <Save className="h-4 w-4 mr-2" /> Save Changes
                      </Button>
                  </DialogFooter> */}
              </DialogContent>
          </Dialog>
      );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-full sm:max-w-3xl lg:max-w-6xl h-[95vh] flex flex-col bg-white border border-gray-200 rounded-lg shadow-lg p-0">
          {/* Header */}
          <DialogHeader className="border-b border-gray-200 pb-4 px-4 sm:px-6 pt-6">
            <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">
              {getModalTitle()}
            </DialogTitle>
          </DialogHeader>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
            {/* Project / Dates - Form fields are here... */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="project">Project *</Label>
                        <span className="text-xs text-gray-500">
                            {formData.project.length}/500
                        </span>
                    </div>
                    <Input
                        id="project"
                        placeholder="test project"
                        value={formData.project}
                        onChange={(e) => {
                          setFormData({ ...formData, project: e.target.value });
                          if (validationErrors.project) {
                            setValidationErrors({ ...validationErrors, project: undefined });
                          }
                        }}
                        maxLength={500}
                        className={validationErrors.project ? 'border-red-500' : ''}
                    />
                    {validationErrors.project && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.project}</p>
                    )}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="startDate">Start Date *</Label>
                    <Input
                        id="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          
                          // If start date is greater than end date, clear the end date
                          if (newStartDate && formData.endDate && newStartDate > formData.endDate) {
                            setFormData({ ...formData, startDate: newStartDate, endDate: "" });
                            // Clear any end date validation errors
                            if (validationErrors.endDate) {
                              setValidationErrors({ ...validationErrors, endDate: undefined });
                            }
                          } else {
                            setFormData({ ...formData, startDate: newStartDate });
                          }
                          
                          // Clear start date error if validation passes
                          if (validationErrors.startDate) {
                            setValidationErrors({ ...validationErrors, startDate: undefined });
                          }
                        }}
                        className={validationErrors.startDate ? 'border-red-500' : ''}
                    />
                    {validationErrors.startDate && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.startDate}</p>
                    )}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="endDate">End Date *</Label>
                    <Input
                        id="endDate"
                        type="date"
                        value={formData.endDate}
                        min={formData.startDate || new Date().toISOString().split("T")[0]}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          setFormData({ ...formData, endDate: newEndDate });
                          
                          // Clear start date error if end date is now valid
                          if (validationErrors.startDate && formData.startDate && newEndDate && formData.startDate <= newEndDate) {
                            setValidationErrors({ ...validationErrors, startDate: undefined });
                          }
                          
                          if (validationErrors.endDate) {
                            setValidationErrors({ ...validationErrors, endDate: undefined });
                          }
                        }}
                        className={validationErrors.endDate ? 'border-red-500' : ''}
                    />
                    {validationErrors.endDate && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.endDate}</p>
                    )}
                </div>
            </div>

            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <Label htmlFor="description">Project Description *</Label>
                    <span className="text-xs text-gray-500">
                        {formData.description.length}/1000
                    </span>
                </div>
                <Textarea
                    id="description"
                    placeholder="test project description"
                    value={formData.description}
                    onChange={(e) => {
                      setFormData({ ...formData, description: e.target.value });
                      if (validationErrors.description) {
                        setValidationErrors({ ...validationErrors, description: undefined });
                      }
                    }}
                    className={`min-h-[100px] ${validationErrors.description ? 'border-red-500' : ''}`}
                    maxLength={1000}
                />
                {validationErrors.description && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.description}</p>
                )}
            </div>
            
            {/* Owners + Members - Select and Badge display... */}
            <div className="space-y-4">
                {/* Owners */}
                {/* Searchable Owners Input */}
                <div className="space-y-2">
                  <Label>Project Owners</Label>
                <div className="task-dropdown-container relative">
                  <div className="relative">
                    {/* Search Bar */}
                    <input
                      type="text"
                      placeholder="Search project owners..."
                      className="w-full border rounded-md px-3 py-2"
                      value={searchTerm}
                      onFocus={() => setDropdownOpen(true)}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setDropdownOpen(true);  // keep list open when typing
                      }}
                    />

                    {/* Dropdown List */}
                    {dropdownOpen && (
                      <div className="absolute z-20 w-full bg-white border rounded-md max-h-48 overflow-y-auto mt-1 shadow">
                        {availableProjectOwners
                          .filter((owner) =>
                            owner?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false
                          )
                          .filter((owner) => !formData.projectOwners.includes(String(owner.id)))
                          .map((owner) => (
                            <div
                              key={owner.id}
                              className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                              onClick={() => {
                                handleOwnerSelect(String(owner.id));
                                setDropdownOpen(false);  // close after selecting
                                setSearchTerm("");       // clear search
                              }}
                            >
                              {owner?.name || 'Unknown'}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* BADGES (unchanged) */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.projectOwners.map((ownerId: string) => {
                      const owner = availableProjectOwners.find((o) => o.id === Number(ownerId));
                      return owner ? (
                        <Badge key={owner.id} variant="secondary" className="flex items-center gap-1">
                          {owner?.name || 'Unknown'}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveOwner(String(owner.id))}
                            className="h-4 w-4 p-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
                </div>

                {/* Members */}
                <div className="space-y-2">
                <Label>Team Members</Label>

                <div className="task-dropdown-container relative" ref={dropdownRef}>
                  <div className="relative">
                    {/* Search Bar */}
                    <input
                      type="text"
                      placeholder="Search team members..."
                      className="w-full border rounded-md px-3 py-2"
                      value={teamSearchTerm}
                      onFocus={() => setTeamDropdownOpen(true)}
                      onChange={(e) => {
                        setTeamSearchTerm(e.target.value);
                        setTeamDropdownOpen(true);
                      }}
                    />

                    {/* Dropdown List */}
                    {teamDropdownOpen && (
                      <div className="absolute z-20 w-full bg-white border rounded-md max-h-48 overflow-y-auto mt-1 shadow">
                        {availableTeamMembers
                          .filter((m) =>
                            m.name.toLowerCase().includes(teamSearchTerm.toLowerCase())
                          )
                          .filter((m) => !formData.teamMembers.includes(String(m.id)))
                          .map((m) => (
                            <div
                              key={m.id}
                              className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                              onClick={() => {
                                handleTeamMemberSelect(String(m.id));
                                setTeamDropdownOpen(false);
                                setTeamSearchTerm("");
                              }}
                            >
                              {m.name}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* BADGES */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.teamMembers.map((memberId: string) => {
                      const member = availableTeamMembers.find(
                        (m) => m.id === Number(memberId)
                      );

                      return member ? (
                        <Badge
                          key={member.id}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {member.name}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveTeamMember(String(member.id))}
                            className="h-4 w-4 p-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
            </div>


            {/* Team members table */}
            {teamMembersTableData.length > 0 && (
                <div className="border rounded-lg overflow-hidden bg-white shadow-sm overflow-x-auto">
                    <Table className="min-w-[500px]">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Team Member</TableHead>
                                <TableHead>Start Date</TableHead>
                                <TableHead>End Date</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {teamMembersTableData.map((member) => (
                                <TableRow key={member.id}>
                                    <TableCell>{member.name}</TableCell>
                                    <TableCell>
                                        <Input
                                            type="date"
                                            value={member.startDate}
                                            onChange={(e) =>
                                                setTeamMembersTableData((prev) =>
                                                    prev.map((m) =>
                                                        m.id === member.id
                                                            ? {
                                                                ...m,
                                                                startDate: e.target.value,
                                                                startDateManuallyEdited: e.target.value !== (formData.startDate || ""),
                                                              }
                                                            : m
                                                    )
                                                )
                                            }
                                            className="h-8 px-2 py-1"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Input
                                            type="date"
                                            value={member.endDate}
                                            onChange={(e) =>
                                                setTeamMembersTableData((prev) =>
                                                    prev.map((m) =>
                                                        m.id === member.id
                                                            ? {
                                                                ...m,
                                                                endDate: e.target.value,
                                                                endDateManuallyEdited: e.target.value !== (formData.endDate || ""),
                                                              }
                                                            : m
                                                    )
                                                )
                                            }
                                            className="h-8 px-2 py-1"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Tasks Section */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                {/* Search */}
                <div className="relative flex items-center w-full sm:w-64">
                  <Search className="absolute left-2 h-4 w-4 text-gray-500" />
                  <Input
                    placeholder="Search for tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
                {/* Add Task */}
                {/* <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                  <Badge variant="secondary" className="self-start sm:self-center">
                    {formData.selectedTasks.length} Tasks Selected
                  </Badge>
                  <Input
                    placeholder="Enter task"
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddTask()}
                    className="w-full sm:w-48"
                  />
                  <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleAddTask} size="sm">
                    <Plus className="h-4 w-4 mr-1" /> Add Task
                  </Button>
                </div> */}
              </div>

              {/* <div className="text-sm text-blue-600">* Blue text indicates tasks that are modified by me</div> */}

              <div className="border rounded-lg p-3 sm:p-4 max-h-64 overflow-y-auto bg-gray-50 shadow-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* 🚀 FilteredTasks now works with names, but we need the full object for the handler */}
                  {filteredTaskNames.map((taskName) => {
                    const task = getTaskObjectByName(taskName);
                    return (
                    <div key={taskName} className="flex items-center space-x-2 justify-between group">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          className="border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                          id={taskName}
                          checked={selectedTaskNames.includes(taskName)}
                          onCheckedChange={() => handleTaskToggle(taskName)}
                        />
                        <label
                          htmlFor={taskName}
                          className={`text-sm flex items-center gap-1 cursor-pointer ${
                            isTaskModifiedByCurrentUser(taskName) ? "text-blue-600" : ""
                          }`}
                        >
                          {taskName}
                        </label>
                      </div>
                      
                      {/* 🚀 NEW: Edit Button */}
                      {/* <Button
                        variant="ghost"
                        size="sm"
                        // Pass the full Task object to the handler
                        onClick={() => handleOpenEditTaskModal(task)} 
                        className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit Task"
                      >
                        <Edit className="h-4 w-4 text-gray-500 hover:text-blue-600" />
                      </Button> */}
                    </div>
                  )})}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t">
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* 🚀 Render the new Edit Task Modal */}
      <EditTaskModal 
          task={taskToEdit}
          isOpen={isEditTaskModalOpen}
          onClose={() => setIsEditTaskModalOpen(false)}
          onSave={(updatedTask) => handleUpdateTask(updatedTask, taskToEdit)}
          currentUser={currentUser}
      />
    </>
  )
}